/**
 * Language detection.
 *
 * The site is bilingual and defaults to Spanish, but most visitors from
 * outside Spain will not read it. Cloudflare already resolves the visitor's
 * country from their IP and exposes it on every proxied zone at
 * /cdn-cgi/trace, so we can ask for it same-origin — no third-party call,
 * and nothing that the page's `connect-src 'self'` CSP would block.
 *
 * Order of precedence:
 *   1. an explicit choice the visitor made with `lang` (remembered)
 *   2. the country behind their IP: ES -> Spanish, anything else -> English
 *   3. the browser's own locale, when the trace is unavailable (local dev,
 *      a non-Cloudflare host, an offline visitor)
 */

const STORAGE_KEY = 'c4sh3r.lang';
const TRACE_URL = '/cdn-cgi/trace';
const TRACE_TIMEOUT_MS = 1500;

export const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'es' || stored === 'en' ? stored : null;
  } catch {
    // private mode, blocked storage — treat as "no preference"
    return null;
  }
};

export const storeLanguage = (lang) => {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // nothing to do; detection simply runs again next visit
  }
};

/** Browser locale, used before the network answers and if it never does. */
export const guessLanguageFromBrowser = () => {
  const tags = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  return tags.some(tag => String(tag).toLowerCase().startsWith('es')) ? 'es' : 'en';
};

/** Country code behind the visitor's IP, or null if it cannot be read. */
const fetchCountry = async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TRACE_TIMEOUT_MS);
  try {
    const res = await fetch(TRACE_URL, { signal: controller.signal, cache: 'no-store' });
    if (!res.ok) return null;
    const match = /^loc=([A-Z]{2})$/m.exec(await res.text());
    return match ? match[1] : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Resolves the language to use. Never overrides a stored choice.
 * Falls back to the browser locale when the country is unknown.
 */
export const detectLanguage = async () => {
  const stored = getStoredLanguage();
  if (stored) return stored;

  const country = await fetchCountry();
  if (country) return country === 'ES' ? 'es' : 'en';

  return guessLanguageFromBrowser();
};
